// src/review/canvas/RiskCanvas.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Stack, Card, Paper, Text, Group, Button, Badge } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconPlayerSkipForward } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useReview } from '../context/ReviewContext';
import RiskOverviewSection from './sections/RiskOverviewSection';
import RiskEvaluationSection from './sections/RiskEvaluationSection';
import MitigationActionsSection from './sections/MitigationActionsSection';
import ResidualRiskSection from './sections/ResidualRiskSection';

/**
 * RiskCanvas - Main canvas for reviewing a single risk
 * Integrates all risk sections with navigation
 */
function RiskCanvas({ risk, currentRiskIndex, totalRisks, onNext, onPrevious, onSkip }) {
  const { modelId } = useReview();

  // Get edit count for this risk
  const reviewState = useSelector((state) =>
    state.reviews.byModel[modelId] || {}
  );
  const edits = reviewState.edits || {};

  // Count edits for this risk across all categories
  const riskEditCount = Object.values(edits)
    .flatMap(category => Object.values(category))
    .filter(edit => edit.itemId === risk.riskId)
    .length;

  return (
    <Stack gap="lg">
      {/* Header with clean title and edit badge */}
      <Paper withBorder radius="sm" p="md" bg="white">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="xs" align="baseline">
              <Text size="lg" fw={700}>{risk.riskName || 'Unnamed Risk'}</Text>
              <Text size="sm" c="dimmed">
                · {currentRiskIndex + 1}/{totalRisks}
              </Text>
            </Group>
            {risk.category && (
              <Text size="xs" c="dimmed">{risk.category}</Text>
            )}
          </Stack>
          {riskEditCount > 0 && (
            <Badge size="lg" variant="filled" color="blue">
              {riskEditCount} {riskEditCount === 1 ? 'Edit' : 'Edits'}
            </Badge>
          )}
        </Group>
      </Paper>

      {/* Risk Overview */}
      <RiskOverviewSection risk={risk} />

      {/* Risk Evaluation */}
      <RiskEvaluationSection risk={risk} />

      {/* Mitigation Actions */}
      <MitigationActionsSection risk={risk} />

      {/* Residual Risk */}
      <ResidualRiskSection risk={risk} />

      {/* Navigation Footer */}
      <Card withBorder p="md">
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={onPrevious}
            disabled={!onPrevious}
          >
            Previous Risk
          </Button>

          <Group gap="sm">
            {onSkip && (
              <Button
                variant="light"
                leftSection={<IconPlayerSkipForward size={18} />}
                onClick={onSkip}
              >
                Skip
              </Button>
            )}
            <Button
              rightSection={<IconArrowRight size={18} />}
              onClick={onNext}
            >
              Next Risk
            </Button>
          </Group>
        </Group>
      </Card>
    </Stack>
  );
}

RiskCanvas.propTypes = {
  risk: PropTypes.shape({
    riskId: PropTypes.string.isRequired,
    riskName: PropTypes.string,
    category: PropTypes.string,
    areaName: PropTypes.string,
    priority: PropTypes.string,
    evaluation: PropTypes.object,
    controls: PropTypes.array,
    residualRisk: PropTypes.object,
  }).isRequired,
  currentRiskIndex: PropTypes.number.isRequired,
  totalRisks: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  onSkip: PropTypes.func,
};

RiskCanvas.defaultProps = {
  onPrevious: null,
  onSkip: null,
};

export default RiskCanvas;
